import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, Select, MenuItem, FormControl, InputLabel,
  IconButton, Stack, Chip, Grid, Tab, Tabs,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { contextApi } from '../api/services';
import type { ExecutiveContext, ContextLayer } from '../types/models';

const layers: ContextLayer[] = ['Profile', 'Strategic', 'Weekly', 'Daily', 'AnnualStrategy'];

const layerColors: Record<ContextLayer, string> = {
  Profile: '#6C9BFF',
  Strategic: '#69F0AE',
  Weekly: '#FFB74D',
  Daily: '#FF8A65',
  AnnualStrategy: '#CE93D8',
};

export default function ContextPage() {
  const [contexts, setContexts] = useState<ExecutiveContext[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editCtx, setEditCtx] = useState<Partial<ExecutiveContext> | null>(null);
  const [tabValue, setTabValue] = useState(0);

  const loadContexts = async () => {
    setLoading(true);
    try {
      setContexts(await contextApi.getAll());
    } catch (err) {
      console.error('Failed to load contexts', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadContexts(); }, []);

  const handleSave = async () => {
    if (!editCtx?.key || !editCtx?.value) return;
    try {
      if (editCtx.id) {
        await contextApi.update(editCtx.id, editCtx);
      } else {
        await contextApi.create(editCtx);
      }
      setDialogOpen(false);
      setEditCtx(null);
      loadContexts();
    } catch (err) {
      console.error('Failed to save context', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this context entry?')) return;
    try {
      await contextApi.delete(id);
      loadContexts();
    } catch (err) {
      console.error('Failed to delete', err);
    }
  };

  const filteredContexts = tabValue === 0
    ? contexts
    : contexts.filter(c => c.layer === layers[tabValue - 1]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4">Context Engine</Typography>
          <Typography variant="subtitle1">Executive context used in all AI planning calls</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => {
          setEditCtx({ layer: 'Daily', key: '', value: '', isActive: true });
          setDialogOpen(true);
        }}>
          Add Context
        </Button>
      </Box>

      <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 2 }}>
        <Tab label={`All (${contexts.length})`} />
        {layers.map(l => (
          <Tab key={l} label={`${l} (${contexts.filter(c => c.layer === l).length})`} />
        ))}
      </Tabs>

      <Grid container spacing={2}>
        {filteredContexts.map((ctx) => (
          <Grid size={{ xs: 12, md: 6 }} key={ctx.id}>
            <Card sx={{ borderLeft: 3, borderColor: layerColors[ctx.layer] || 'primary.main' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                      <Chip label={ctx.layer} size="small" sx={{ bgcolor: `${layerColors[ctx.layer]}20`, color: layerColors[ctx.layer] }} />
                      <Typography variant="subtitle2">{ctx.key}</Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{ctx.value}</Typography>
                  </Box>
                  <Stack direction="row">
                    <IconButton size="small" onClick={() => { setEditCtx(ctx); setDialogOpen(true); }}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => handleDelete(ctx.id)}><DeleteIcon fontSize="small" /></IconButton>
                  </Stack>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editCtx?.id ? 'Edit Context' : 'New Context'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Layer</InputLabel>
              <Select value={editCtx?.layer || 'Daily'} label="Layer" onChange={e => setEditCtx(prev => ({ ...prev, layer: e.target.value as ContextLayer }))}>
                {layers.map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label="Key" fullWidth value={editCtx?.key || ''} onChange={e => setEditCtx(prev => ({ ...prev, key: e.target.value }))} placeholder="e.g., Current Focus, Team Priority" />
            <TextField label="Value" fullWidth multiline rows={4} value={editCtx?.value || ''} onChange={e => setEditCtx(prev => ({ ...prev, value: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
